import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiSave,
  FiRefreshCw,
  FiBell,
  FiMail,
  FiShield,
  FiCreditCard,
  FiMapPin,
  FiGlobe,
  FiDatabase,
  FiUsers,
  FiSettings as FiSettingsIcon,
  FiCheck,
  FiX,
} from "react-icons/fi";

const SettingsContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 0;
`;

const SettingsContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;

  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 1.125rem;
  }
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SettingsSection = styled(motion.div)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 1rem;

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: linear-gradient(135deg, #2d5016, #4a7c59);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.25rem;
  }

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d5016;
    margin: 0;
  }
`;

const SectionContent = styled.div`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    font-weight: 500;
    color: #333;
    margin-bottom: 0.5rem;
  }

  input,
  select,
  textarea {
    width: 100%;
    padding: 0.75rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: #2d5016;
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .checkbox-group {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;

    input[type="checkbox"] {
      width: auto;
      margin: 0;
    }

    label {
      margin: 0;
      font-weight: normal;
    }
  }

  .help-text {
    font-size: 0.875rem;
    color: #666;
    margin-top: 0.25rem;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid #2d5016;
  background: ${(props) => (props.primary ? "#2d5016" : "white")};
  color: ${(props) => (props.primary ? "white" : "#2d5016")};
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.primary ? "#1a3a0e" : "#f8f9fa")};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const StatusMessage = styled(motion.div)`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;

  &.success {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }

  &.error {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
`;

const Settings = () => {
  const [settings, setSettings] = useState({
    // Platform Settings
    platformName: "NourishGY",
    platformDescription: "Guyana's premier meal subscription platform",
    platformEmail: "admin@nourishgy.com",
    platformPhone: "+592-123-4567",
    platformAddress: "Georgetown, Guyana",

    // Notification Settings
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    orderUpdates: true,
    vendorUpdates: true,
    systemAlerts: true,

    // Payment Settings
    stripePublicKey: "",
    stripeSecretKey: "",
    mmgApiKey: "",
    mmgMerchantId: "",
    currency: "GYD",
    taxRate: 16.5,

    // Delivery Settings
    defaultDeliveryRadius: 10,
    deliveryFee: 5.0,
    freeDeliveryThreshold: 50.0,
    deliveryTimeSlots: "9:00 AM - 6:00 PM",

    // Security Settings
    sessionTimeout: 30,
    requireTwoFactor: false,
    passwordMinLength: 8,
    maxLoginAttempts: 5,

    // Database Settings
    backupFrequency: "daily",
    retentionPeriod: 90,
    autoBackup: true,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  const handleInputChange = (section, field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setStatusMessage(null);

    try {
      // This will be replaced with actual API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setStatusMessage({
        type: "success",
        message: "Settings saved successfully!",
      });
    } catch (error) {
      setStatusMessage({
        type: "error",
        message: "Failed to save settings. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setStatusMessage(null);
    // Reset to default values
    window.location.reload();
  };

  return (
    <SettingsContainer>
      <SettingsContent>
        <PageHeader>
          <h1>Platform Settings</h1>
          <p>Configure platform settings, notifications, and integrations</p>
        </PageHeader>

        {statusMessage && (
          <StatusMessage
            className={statusMessage.type}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {statusMessage.type === "success" ? <FiCheck /> : <FiX />}
            {statusMessage.message}
          </StatusMessage>
        )}

        <SettingsGrid>
          {/* Platform Settings */}
          <SettingsSection
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <SectionHeader>
              <div className="icon">
                <FiGlobe />
              </div>
              <h2>Platform Information</h2>
            </SectionHeader>
            <SectionContent>
              <FormGroup>
                <label>Platform Name</label>
                <input
                  type="text"
                  value={settings.platformName}
                  onChange={(e) =>
                    handleInputChange(
                      "platform",
                      "platformName",
                      e.target.value
                    )
                  }
                />
              </FormGroup>

              <FormGroup>
                <label>Description</label>
                <textarea
                  value={settings.platformDescription}
                  onChange={(e) =>
                    handleInputChange(
                      "platform",
                      "platformDescription",
                      e.target.value
                    )
                  }
                />
              </FormGroup>

              <div className="form-row">
                <FormGroup>
                  <label>Contact Email</label>
                  <input
                    type="email"
                    value={settings.platformEmail}
                    onChange={(e) =>
                      handleInputChange(
                        "platform",
                        "platformEmail",
                        e.target.value
                      )
                    }
                  />
                </FormGroup>
                <FormGroup>
                  <label>Contact Phone</label>
                  <input
                    type="tel"
                    value={settings.platformPhone}
                    onChange={(e) =>
                      handleInputChange(
                        "platform",
                        "platformPhone",
                        e.target.value
                      )
                    }
                  />
                </FormGroup>
              </div>

              <FormGroup>
                <label>Platform Address</label>
                <input
                  type="text"
                  value={settings.platformAddress}
                  onChange={(e) =>
                    handleInputChange(
                      "platform",
                      "platformAddress",
                      e.target.value
                    )
                  }
                />
              </FormGroup>
            </SectionContent>
          </SettingsSection>

          {/* Notification Settings */}
          <SettingsSection
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <SectionHeader>
              <div className="icon">
                <FiBell />
              </div>
              <h2>Notification Settings</h2>
            </SectionHeader>
            <SectionContent>
              <FormGroup>
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="emailNotifications"
                    checked={settings.emailNotifications}
                    onChange={(e) =>
                      handleInputChange(
                        "notifications",
                        "emailNotifications",
                        e.target.checked
                      )
                    }
                  />
                  <label htmlFor="emailNotifications">
                    Email Notifications
                  </label>
                </div>
              </FormGroup>

              <FormGroup>
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="smsNotifications"
                    checked={settings.smsNotifications}
                    onChange={(e) =>
                      handleInputChange(
                        "notifications",
                        "smsNotifications",
                        e.target.checked
                      )
                    }
                  />
                  <label htmlFor="smsNotifications">SMS Notifications</label>
                </div>
              </FormGroup>

              <FormGroup>
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="pushNotifications"
                    checked={settings.pushNotifications}
                    onChange={(e) =>
                      handleInputChange(
                        "notifications",
                        "pushNotifications",
                        e.target.checked
                      )
                    }
                  />
                  <label htmlFor="pushNotifications">Push Notifications</label>
                </div>
              </FormGroup>

              <FormGroup>
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="orderUpdates"
                    checked={settings.orderUpdates}
                    onChange={(e) =>
                      handleInputChange(
                        "notifications",
                        "orderUpdates",
                        e.target.checked
                      )
                    }
                  />
                  <label htmlFor="orderUpdates">Order Updates</label>
                </div>
              </FormGroup>

              <FormGroup>
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="vendorUpdates"
                    checked={settings.vendorUpdates}
                    onChange={(e) =>
                      handleInputChange(
                        "notifications",
                        "vendorUpdates",
                        e.target.checked
                      )
                    }
                  />
                  <label htmlFor="vendorUpdates">Vendor Updates</label>
                </div>
              </FormGroup>

              <FormGroup>
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="systemAlerts"
                    checked={settings.systemAlerts}
                    onChange={(e) =>
                      handleInputChange(
                        "notifications",
                        "systemAlerts",
                        e.target.checked
                      )
                    }
                  />
                  <label htmlFor="systemAlerts">System Alerts</label>
                </div>
              </FormGroup>
            </SectionContent>
          </SettingsSection>

          {/* Payment Settings */}
          <SettingsSection
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <SectionHeader>
              <div className="icon">
                <FiCreditCard />
              </div>
              <h2>Payment Settings</h2>
            </SectionHeader>
            <SectionContent>
              <FormGroup>
                <label>Stripe Public Key</label>
                <input
                  type="text"
                  value={settings.stripePublicKey}
                  onChange={(e) =>
                    handleInputChange(
                      "payment",
                      "stripePublicKey",
                      e.target.value
                    )
                  }
                  placeholder="pk_test_..."
                />
                <div className="help-text">Your Stripe publishable key</div>
              </FormGroup>

              <FormGroup>
                <label>Stripe Secret Key</label>
                <input
                  type="password"
                  value={settings.stripeSecretKey}
                  onChange={(e) =>
                    handleInputChange(
                      "payment",
                      "stripeSecretKey",
                      e.target.value
                    )
                  }
                  placeholder="sk_test_..."
                />
                <div className="help-text">
                  Your Stripe secret key (keep secure)
                </div>
              </FormGroup>

              <FormGroup>
                <label>MMG API Key</label>
                <input
                  type="password"
                  value={settings.mmgApiKey}
                  onChange={(e) =>
                    handleInputChange("payment", "mmgApiKey", e.target.value)
                  }
                  placeholder="Your MMG API key"
                />
                <div className="help-text">Mobile Money Guyana API key</div>
              </FormGroup>

              <div className="form-row">
                <FormGroup>
                  <label>Currency</label>
                  <select
                    value={settings.currency}
                    onChange={(e) =>
                      handleInputChange("payment", "currency", e.target.value)
                    }
                  >
                    <option value="GYD">Guyanese Dollar (GYD)</option>
                    <option value="USD">US Dollar (USD)</option>
                  </select>
                </FormGroup>
                <FormGroup>
                  <label>Tax Rate (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={settings.taxRate}
                    onChange={(e) =>
                      handleInputChange(
                        "payment",
                        "taxRate",
                        parseFloat(e.target.value)
                      )
                    }
                  />
                </FormGroup>
              </div>
            </SectionContent>
          </SettingsSection>

          {/* Delivery Settings */}
          <SettingsSection
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <SectionHeader>
              <div className="icon">
                <FiMapPin />
              </div>
              <h2>Delivery Settings</h2>
            </SectionHeader>
            <SectionContent>
              <FormGroup>
                <label>Default Delivery Radius (km)</label>
                <input
                  type="number"
                  value={settings.defaultDeliveryRadius}
                  onChange={(e) =>
                    handleInputChange(
                      "delivery",
                      "defaultDeliveryRadius",
                      parseInt(e.target.value)
                    )
                  }
                />
              </FormGroup>

              <div className="form-row">
                <FormGroup>
                  <label>Delivery Fee (GYD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={settings.deliveryFee}
                    onChange={(e) =>
                      handleInputChange(
                        "delivery",
                        "deliveryFee",
                        parseFloat(e.target.value)
                      )
                    }
                  />
                </FormGroup>
                <FormGroup>
                  <label>Free Delivery Threshold (GYD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={settings.freeDeliveryThreshold}
                    onChange={(e) =>
                      handleInputChange(
                        "delivery",
                        "freeDeliveryThreshold",
                        parseFloat(e.target.value)
                      )
                    }
                  />
                </FormGroup>
              </div>

              <FormGroup>
                <label>Delivery Time Slots</label>
                <input
                  type="text"
                  value={settings.deliveryTimeSlots}
                  onChange={(e) =>
                    handleInputChange(
                      "delivery",
                      "deliveryTimeSlots",
                      e.target.value
                    )
                  }
                  placeholder="9:00 AM - 6:00 PM"
                />
              </FormGroup>
            </SectionContent>
          </SettingsSection>

          {/* Security Settings */}
          <SettingsSection
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <SectionHeader>
              <div className="icon">
                <FiShield />
              </div>
              <h2>Security Settings</h2>
            </SectionHeader>
            <SectionContent>
              <FormGroup>
                <label>Session Timeout (minutes)</label>
                <input
                  type="number"
                  value={settings.sessionTimeout}
                  onChange={(e) =>
                    handleInputChange(
                      "security",
                      "sessionTimeout",
                      parseInt(e.target.value)
                    )
                  }
                />
              </FormGroup>

              <FormGroup>
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="requireTwoFactor"
                    checked={settings.requireTwoFactor}
                    onChange={(e) =>
                      handleInputChange(
                        "security",
                        "requireTwoFactor",
                        e.target.checked
                      )
                    }
                  />
                  <label htmlFor="requireTwoFactor">
                    Require Two-Factor Authentication
                  </label>
                </div>
              </FormGroup>

              <div className="form-row">
                <FormGroup>
                  <label>Minimum Password Length</label>
                  <input
                    type="number"
                    value={settings.passwordMinLength}
                    onChange={(e) =>
                      handleInputChange(
                        "security",
                        "passwordMinLength",
                        parseInt(e.target.value)
                      )
                    }
                  />
                </FormGroup>
                <FormGroup>
                  <label>Max Login Attempts</label>
                  <input
                    type="number"
                    value={settings.maxLoginAttempts}
                    onChange={(e) =>
                      handleInputChange(
                        "security",
                        "maxLoginAttempts",
                        parseInt(e.target.value)
                      )
                    }
                  />
                </FormGroup>
              </div>
            </SectionContent>
          </SettingsSection>

          {/* Database Settings */}
          <SettingsSection
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <SectionHeader>
              <div className="icon">
                <FiDatabase />
              </div>
              <h2>Database Settings</h2>
            </SectionHeader>
            <SectionContent>
              <FormGroup>
                <label>Backup Frequency</label>
                <select
                  value={settings.backupFrequency}
                  onChange={(e) =>
                    handleInputChange(
                      "database",
                      "backupFrequency",
                      e.target.value
                    )
                  }
                >
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </FormGroup>

              <FormGroup>
                <label>Data Retention Period (days)</label>
                <input
                  type="number"
                  value={settings.retentionPeriod}
                  onChange={(e) =>
                    handleInputChange(
                      "database",
                      "retentionPeriod",
                      parseInt(e.target.value)
                    )
                  }
                />
              </FormGroup>

              <FormGroup>
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="autoBackup"
                    checked={settings.autoBackup}
                    onChange={(e) =>
                      handleInputChange(
                        "database",
                        "autoBackup",
                        e.target.checked
                      )
                    }
                  />
                  <label htmlFor="autoBackup">Enable Automatic Backups</label>
                </div>
              </FormGroup>
            </SectionContent>
          </SettingsSection>
        </SettingsGrid>

        <ButtonGroup>
          <Button onClick={handleReset}>
            <FiRefreshCw />
            Reset
          </Button>
          <Button primary onClick={handleSave} disabled={isSaving}>
            <FiSave />
            {isSaving ? "Saving..." : "Save Settings"}
          </Button>
        </ButtonGroup>
      </SettingsContent>
    </SettingsContainer>
  );
};

export default Settings;
